import { Link } from "react-router-dom";
import { ArrowRight, Phone, CheckCircle2, HelpCircle, Home } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { BRAND, IMAGES } from "@/data/site";
import { useT } from "@/i18n/I18nProvider";
import { useSite } from "@/i18n/useSite";
import { PageIntro, SectionHeading, Reveal } from "@/components/Section";
import { TalkCta } from "@/components/TalkCta";

const TILES = ["tile-sun corner-tr", "tile-sky corner-bl", "tile-coral corner-tr", "tile-mint corner-bl"];
const PROGRAM_TILES = ["tile-blue corner-tr", "tile-sun corner-bl", "tile-sky corner-tr"];

const Eligibility = () => {
  const t = useT();
  const { AGED_CARE } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="agedcare-eligibility">
      <div className="container-x">
        <SectionHeading eyebrow={t("Am I eligible?")} title={t("Who can get help at home")} text={t("Help at home through My Aged Care is for older Australians who need some support to keep living independently.")} />
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {AGED_CARE.eligibility.map((e, i) => {
            const light = TILES[i].includes("tile-coral");
            return (
              <Reveal key={i} delay={i * 0.07} className="h-full">
                <div className={`${TILES[i]} p-7 h-full`} data-testid={`agedcare-eligibility-${i}`}>
                  <CheckCircle2 size={28} className={light ? "text-white" : "text-[var(--blue)]"} />
                  <h3 className={`mt-4 text-xl font-semibold ${light ? "!text-white" : "!text-[var(--ink)]"}`}>{e.title}</h3>
                  <p className={`mt-2 text-[15px] leading-relaxed ${light ? "text-white/85" : "text-[var(--ink)]/80"}`}>{e.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.2}><p className="mt-8 text-sm text-[var(--muted)]">{t("Not sure?")} <Link to="/contact" className="link-u" data-testid="agedcare-eligibility-contact">{t("Contact us")}</Link> — {t("or visit")} <a href="https://www.myagedcare.gov.au/types-care/help-home" target="_blank" rel="noreferrer" className="link-u">myagedcare.gov.au</a>.</p></Reveal>
      </div>
    </section>
  );
};

const Programs = () => {
  const t = useT();
  const { AGED_CARE } = useSite();
  return (
    <section className="py-16 lg:py-24 bg-[var(--blue-tint)]" data-testid="agedcare-programs">
      <div className="container-x">
        <SectionHeading eyebrow={t("Types of help at home")} title={t("Government programs that fund care at home")} text={t("The Australian Government subsidises home support services. Which program you access depends on your assessed needs — Aid in Care can deliver supports under each.")} />
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
          {AGED_CARE.programs.map((p, i) => {
            const light = PROGRAM_TILES[i].includes("tile-blue");
            return (
              <Reveal key={i} delay={i * 0.08} className="h-full">
                <div className={`${PROGRAM_TILES[i]} p-8 h-full`} data-testid={`agedcare-program-${i}`}>
                  <span className={`w-12 h-12 rounded-md flex items-center justify-center ${light ? "bg-white/20 text-white" : "bg-white text-[var(--blue)]"}`}><Home size={22} /></span>
                  <h3 className={`mt-5 text-xl font-semibold ${light ? "!text-white" : "!text-[var(--ink)]"}`}>{p.title}</h3>
                  <p className={`mt-2 text-[15px] leading-relaxed ${light ? "text-white/85" : "text-[var(--ink)]/80"}`}>{p.text}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const HowToApply = () => {
  const t = useT();
  const { AGED_CARE } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="agedcare-steps" id="how-to-apply">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <SectionHeading eyebrow={t("How to apply")} title={t("Six steps to getting help at home")} text={t("Everything starts with a free assessment through My Aged Care. We can help you with every step.")} />
          <Reveal delay={0.1} className="mt-8"><img src={IMAGES.highCare} alt={t("Carer and older woman talking at home")} className="w-full h-64 object-cover" style={{ borderRadius: "0 64px 0 64px" }} /></Reveal>
        </div>
        <ol className="lg:col-span-8 space-y-4">
          {AGED_CARE.steps.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <li className="bg-[var(--blue-tint)] corner-tr-sm p-6 flex gap-5" data-testid={`agedcare-step-${i}`}>
                <span className="w-11 h-11 rounded-md bg-[var(--coral)] text-white font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div><h3 className="text-lg font-semibold !text-[var(--blue)]">{s.title}</h3><p className="mt-1.5 text-[15px] text-[var(--body)] leading-relaxed">{s.text}</p></div>
              </li>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
};

const Services = () => {
  const t = useT();
  const { AGED_CARE } = useSite();
  return (
    <section className="py-16 lg:py-24 bg-[var(--blue)] text-white" data-testid="agedcare-services">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <Reveal className="lg:col-span-5 order-2 lg:order-1">
          <img src={IMAGES.garden} alt={t("Support worker helping in the garden")} className="w-full h-[320px] lg:h-[420px] object-cover" style={{ borderRadius: "0 80px 0 80px" }} />
        </Reveal>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <SectionHeading light eyebrow={t("How Aid in Care can help")} title={t("Help at home, delivered by people you'll come to know")} text={t("Asking for help doesn't mean losing your independence — quite the opposite. A little support with daily activities means you can stay in your own home, on your own terms, for longer.")} />
          <Reveal delay={0.1}>
            <ul className="mt-8 grid sm:grid-cols-2 gap-3 text-[15px]">
              {AGED_CARE.services.map((s, i) => <li key={i} className="flex items-start gap-3"><CheckCircle2 size={18} className="text-[var(--sun)] mt-0.5 shrink-0" />{s}</li>)}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="btn btn-coral" data-testid="agedcare-contact">{t("Ask us about help at home")} <ArrowRight size={16} /></Link>
              <a href={`tel:${BRAND.phone.tel}`} className="btn btn-outline-light"><Phone size={16} /> {BRAND.phone.number}</a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Faqs = () => {
  const t = useT();
  const { AGED_CARE } = useSite();
  return (
    <section className="py-16 lg:py-24" data-testid="agedcare-faqs">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4"><SectionHeading eyebrow={t("Common questions")} title={t("Help at home frequently asked questions")} /></div>
        <Reveal className="lg:col-span-8">
          <Accordion type="single" collapsible className="divide-y divide-slate-200 border-y border-slate-200">
            {AGED_CARE.faqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-0">
                <AccordionTrigger className="text-start text-base sm:text-lg font-semibold text-[var(--blue)] hover:no-underline py-5" data-testid={`agedcare-faq-trigger-${i}`}>
                  <span className="flex items-start gap-3"><HelpCircle size={20} className="text-[var(--coral)] mt-0.5 shrink-0" />{f.q}</span>
                </AccordionTrigger>
                <AccordionContent className="text-[15px] text-[var(--body)] leading-relaxed ps-8 pb-5" data-testid={`agedcare-faq-content-${i}`}>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </div>
    </section>
  );
};

export default function MyAgedCare() {
  const t = useT();
  return (
    <>
      <PageIntro eyebrow={t("My Aged Care")} title={t("Help at home for")} accent={t("older Australians.")} text={t("As we get older, living independently can become harder. Through My Aged Care, the Australian Government subsidises support in your own home — and Aid in Care can deliver it. Here's who's eligible, which programs exist, how to apply, and how we can help.")} image={IMAGES.hero}>
        <div className="mt-8 flex flex-wrap gap-3">
          <a href="#how-to-apply" className="btn btn-coral" data-testid="agedcare-how-to-apply">{t("How to apply")} <ArrowRight size={16} /></a>
          <Link to="/contact" className="btn btn-ghost-blue" data-testid="agedcare-enquire">{t("Talk to us")}</Link>
        </div>
      </PageIntro>
      <Eligibility />
      <Programs />
      <HowToApply />
      <Services />
      <Faqs />
      <TalkCta sourcePage="my-aged-care" />
    </>
  );
}
